<template>
  <div class="travel-page" :class="{ 'travel-page--customer': channel === 'customer' }">
      <header class="travel-header">
        <div class="travel-header-left">
          <button type="button" class="back-btn" @click="goBack">
            <v-icon size="18" color="#1E4276">mdi-arrow-left</v-icon>
          </button>
          <div class="travel-header-text">
            <p class="breadcrumb">{{ travelConfig.labels.saleBreadcrumb }}</p>
            <h1 class="title">{{ travelConfig.labels.saleTitle }}</h1>
          </div>
        </div>
        <button type="button" class="btn-new-sale" @click="showNewSaleConfirm = true">
          <v-icon x-small color="#1E4276" class="tw-mr-1">mdi-refresh</v-icon>
          {{ travelConfig.labels.newSale }}
        </button>
      </header>

      <referral-agent-banner v-if="channel === 'customer'" />

      <v-dialog v-model="showNewSaleConfirm" max-width="380" overlay-opacity="0.5">
        <div class="new-sale-dialog">
          <div class="nsd-header">
            <div class="nsd-icon">
              <v-icon size="24" color="white">mdi-alert-circle-outline</v-icon>
            </div>
            <div>
              <p class="nsd-title">Start a New Sale?</p>
              <p class="nsd-sub">Travel insurance</p>
            </div>
          </div>
          <div class="nsd-body">
            <p class="nsd-desc">{{ travelConfig.labels.newSaleSub }}</p>
          </div>
          <div class="nsd-footer">
            <button type="button" class="nsd-btn-cancel" @click="showNewSaleConfirm = false">Keep Working</button>
            <button type="button" class="nsd-btn-confirm" @click="startNewSale">
              <v-icon x-small color="white" class="tw-mr-1">mdi-refresh</v-icon>
              Yes, Start New
            </button>
          </div>
        </div>
      </v-dialog>

      <div v-if="loading" class="loading-wrap">
        <v-progress-circular indeterminate color="#00AEEF" />
        <span>Loading quote…</span>
      </div>

      <div v-else-if="quoteLoadError" class="loading-wrap loading-wrap--error">
        <v-icon color="#DC2626" class="tw-mr-2">mdi-alert-circle-outline</v-icon>
        <div>
          <p class="load-error-text">{{ quoteLoadError }}</p>
          <button type="button" class="btn-outline tw-mt-3" @click="goBack">Back to quote</button>
        </div>
      </div>

      <template v-else>
        <div class="step-progress">
          <div
            v-for="(label, i) in steps"
            :key="label"
            class="step-item"
            :class="{ active: activeStep === i, done: activeStep > i }"
          >
            <span class="step-dot">{{ activeStep > i ? '✓' : i + 1 }}</span>
            <span class="step-label">{{ label }}</span>
          </div>
        </div>

        <div
          class="quote-layout"
          :class="{ 'quote-layout--checkout': channel === 'customer' && activeStep === 2 }"
        >
          <div class="quote-form-column">
            <!-- Step 1: Travellers — Mapfre getPrice uses real insuredData here -->
            <div v-show="activeStep === 0" class="section-card">
              <div class="section-header">
                <span class="section-number">1</span>
                <h3>Travellers</h3>
              </div>

              <div v-if="accountPolicyholder" class="policyholder-card">
                <p class="subsection-title">Policyholder</p>
                <v-progress-linear
                  v-if="loadingPolicyholderIprs"
                  indeterminate
                  color="#00AEEF"
                  class="tw-mb-3"
                />
                <div class="pa-form-fields">
                  <div class="form-row">
                    <div class="input-group">
                      <label class="input-label">First name <span class="req">*</span></label>
                      <v-text-field
                        :value="accountPolicyholder.firstName"
                        outlined dense hide-details="auto"
                        class="custom-input"
                        maxlength="40"
                        :readonly="channel === 'customer'"
                        @input="updateAccountPolicyholder('firstName', $event)"
                      />
                    </div>
                    <div class="input-group">
                      <label class="input-label">Surname <span class="req">*</span></label>
                      <v-text-field
                        :value="accountPolicyholder.surname"
                        outlined dense hide-details="auto"
                        class="custom-input"
                        maxlength="40"
                        :readonly="channel === 'customer'"
                        @input="updateAccountPolicyholder('surname', $event)"
                      />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="input-group">
                      <label class="input-label">Date of birth</label>
                      <v-text-field
                        v-if="accountPolicyholder.dateOfBirth && channel === 'customer'"
                        :value="accountPolicyholder.dateOfBirth"
                        type="date"
                        outlined dense hide-details="auto"
                        class="custom-input"
                        readonly
                      />
                      <v-text-field
                        v-else
                        :value="accountPolicyholder.dateOfBirth"
                        type="date"
                        :max="maxPolicyholderDob"
                        outlined dense hide-details="auto"
                        class="custom-input"
                        :hint="channel === 'customer' ? 'Could not load from ID — enter manually' : ''"
                        :persistent-hint="channel === 'customer'"
                        @input="updateAccountPolicyholder('dateOfBirth', $event)"
                      />
                    </div>
                    <div class="input-group">
                      <label class="input-label">Passport <span class="req">*</span></label>
                      <v-text-field
                        :value="accountPolicyholder.passport"
                        outlined dense
                        class="custom-input"
                        :maxlength="TRAVEL_PASSPORT_MAX_LEN"
                        placeholder="e.g. A1234567"
                        :error-messages="policyholderPassportError"
                        :error="policyholderPassportError.length > 0"
                        @input="updateAccountPolicyholder('passport', $event)"
                      />
                    </div>
                  </div>
                  <label class="travelling-toggle">
                    <input
                      v-model="policyholderIsTravelling"
                      type="checkbox"
                      @change="onPolicyholderTravellingChange"
                    >
                    <span>{{ channel === 'customer' ? 'I am also travelling on this trip' : 'Policyholder is also travelling on this trip' }}</span>
                  </label>
                  <p class="travelling-toggle-hint">Ticking this adds the policyholder as another traveller and updates the price.</p>
                </div>
              </div>

              <travel-traveller-form
                :travellers="travellers"
                :max-travellers="travellerCount"
                fixed-count
                lock-quote-dobs
                :external-policyholder="!!accountPolicyholder"
                @update:travellers="onTravellersUpdate"
                @policyholder-error="onPolicyholderError"
              />
              <p v-if="repricing" class="reprice-hint">
                <v-progress-circular indeterminate size="14" width="2" color="#00AEEF" class="tw-mr-2" />
                Updating premium with Mapfre…
              </p>
              <div class="actions-row">
                <button type="button" class="btn-outline" @click="goBack">Back to quote</button>
                <button
                  type="button"
                  class="btn-primary"
                  :disabled="!canContinueFromTravellers"
                  @click="activeStep = 1"
                >
                  Continue
                </button>
              </div>
              <p v-if="!canContinueFromTravellers && travellersContinueHint" class="step-hint step-hint--warn">
                {{ travellersContinueHint }}
              </p>
            </div>

            <!-- Step 2: Contact -->
            <div v-show="activeStep === 1" class="section-card">
              <div class="section-header">
                <span class="section-number">2</span>
                <h3>{{ travelConfig.labels.contactSection }}</h3>
              </div>
              <p class="step-hint">{{ travelConfig.labels.contactHint }}</p>
              <div class="pa-form-fields">
                <div class="form-row">
                  <div class="input-group">
                    <label class="input-label">Email <span class="req">*</span></label>
                    <v-text-field
                      v-model="customerEmail"
                      type="email"
                      outlined dense hide-details="auto"
                      class="custom-input"
                      placeholder="customer@email.com"
                      :error-messages="agentEmailError"
                    />
                  </div>
                  <div class="input-group">
                    <label class="input-label">M-Pesa phone <span class="req">*</span></label>
                    <v-text-field
                      v-model="phone"
                      placeholder="07XXXXXXXX"
                      outlined dense hide-details="auto"
                      class="custom-input"
                      maxlength="12"
                      :error-messages="agentPhoneError"
                    />
                  </div>
                </div>
              </div>

              <div class="passport-uploads tw-mt-5">
                <p class="subsection-title">Passport copies</p>
                <p class="step-hint tw-mb-3">
                  Upload a clear passport copy for each person below (PDF, JPEG or PNG).
                </p>
                <div v-if="accountPolicyholder" class="passport-upload-card">
                  <p class="passport-upload-label">
                    Policyholder — {{ policyholderLabel }}
                  </p>
                  <upload-zone
                    label="Passport copy"
                    required
                    :upload-obj="policyholderPassportUpload"
                    @file-selected="handlePolicyholderPassportSelect"
                    @remove="clearPolicyholderPassportUpload"
                  />
                </div>
                <div
                  v-for="(traveller, idx) in travellers"
                  :key="'passport-upload-' + idx"
                  class="passport-upload-card"
                >
                  <p class="passport-upload-label">
                    Traveller {{ idx + 1 }} — {{ travellerLabel(traveller) }}
                  </p>
                  <upload-zone
                    label="Passport copy"
                    required
                    :upload-obj="passportUploadFor(idx)"
                    @file-selected="(f) => handlePassportFileSelect(f, idx)"
                    @remove="clearPassportUpload(idx)"
                  />
                </div>
              </div>
              <div class="actions-row">
                <button type="button" class="btn-outline" @click="activeStep = 0">Back</button>
                <button
                  type="button"
                  class="btn-primary"
                  :disabled="!canContinueFromContact"
                  @click="continueFromContact"
                >
                  Continue to payment
                </button>
              </div>
              <p v-if="!canContinueFromContact && contactContinueHint" class="step-hint step-hint--warn">
                {{ contactContinueHint }}
              </p>
            </div>

            <!-- Step 3: Payment — issuance runs server-side after M-Pesa success -->
            <div v-show="activeStep === 2" class="section-card section-card--payment">
              <div class="section-header">
                <span class="section-number">3</span>
                <h3>Review &amp; pay</h3>
              </div>

              <div class="pay-summary-card tw-mb-5">
                <div class="pay-summary-head">
                  <div class="pay-summary-head-icon">
                    <v-icon x-small color="white">mdi-airplane</v-icon>
                  </div>
                  <span>{{ quote.productLabel || 'Travel cover' }}</span>
                </div>
                <div class="pay-summary-section">
                  <p class="pay-sec-label">Trip</p>
                  <div class="pay-sum-row">
                    <span class="pay-sum-label">Travel dates</span>
                    <span class="pay-sum-value">{{ quote.startDate }} → {{ quote.endDate }}</span>
                  </div>
                  <div class="pay-sum-row">
                    <span class="pay-sum-label">Travellers</span>
                    <span class="pay-sum-value">{{ pricedTravellerCount }}</span>
                  </div>
                  <div class="pay-sum-row">
                    <span class="pay-sum-label">Quote ref</span>
                    <span class="pay-sum-value">{{ quoteId }}</span>
                  </div>
                </div>
                <div class="pay-premium-footer">
                  <div class="pay-prem-total">
                    <span>Amount due</span>
                    <span class="pay-prem-total-val">KES {{ formatMoney(quote.kshAmount) }}</span>
                  </div>
                  <p class="pay-usd-note">{{ quote.grossUsd }} USD @ {{ quote.fxRate }}</p>
                </div>
              </div>

              <v-checkbox v-model="declaration" hide-details class="pay-decl-check tw-mb-4">
                <template #label>
                  <span class="decl-text">{{ travelConfig.labels.declaration }}</span>
                </template>
              </v-checkbox>

              <travel-payment-methods
                :paying="paying"
                :polling="polling"
                :verifying-paybill="verifyingPaybill"
                :poll-timed-out="pollTimedOut && !!activePaymentId"
                :show-manual-check="!!activePaymentId && !paying"
                :declarations-accepted="declaration"
                :ready-to-pay="canPay"
                :payment-reference="paymentReference"
                :formatted-premium="formattedPremium"
                :mpesa-phone.sync="phone"
                :active-pay-tab.sync="activePayTab"
                :paybill-fallback="paybillFallback"
                :ussd-steps="ussdSteps"
                @pay-mpesa="requestPayment"
                @verify-paybill="verifyPaybillPayment"
                @check-payment="manualCheckPayment"
              />
              <p v-if="!canPay && paymentBlockedHint" class="step-hint step-hint--warn tw-mt-3">
                {{ paymentBlockedHint }}
              </p>

              <div class="actions-row tw-mt-4">
                <button type="button" class="btn-outline" @click="activeStep = 1">Back</button>
              </div>
            </div>
          </div>

          <aside class="quote-sidebar">
            <travel-quote-sidebar
              :gross-usd="quote.grossUsd"
              :fx-rate="quote.fxRate"
              :summary-lines="summaryLines"
              :quote-snapshot="quote"
              :price-calculated="!!quote.grossUsd"
              :pricing="repricing"
              continue-label=""
            />
          </aside>
        </div>
      </template>
    </div>
</template>

<script>
import UploadZone from '@/components/agent/UploadZone';
import TravelQuoteSidebar from '@/components/agent/travel/TravelQuoteSidebar';
import TravelTravellerForm from '@/components/agent/travel/TravelTravellerForm';
import TravelPaymentMethods from '@/components/agent/travel/TravelPaymentMethods';
import agentTravelApi, { isRemoteTravelQuoteId, travelPaymentReference, resolveTravelCustomerIdNo } from '@/services/agentTravelApi';
import { referralAgencyNumericId } from '@/services/referralApi';
import ReferralAgentBanner from '@/components/shared/ReferralAgentBanner';
import {
  loadTravelPendingQuote,
  saveTravelPendingQuote,
  saveTravelSaleProgress,
  loadTravelSaleProgress,
  clearTravelQuoteCache,
} from '@/utils/saleCache';
import { invalidateAfterPayment } from '@/utils/portalBootstrap';
import { ZONE_LABELS } from '@/constants/travelProducts';
import {
  applyIprsToTravellerRow,
  buildAccountHolderTraveller,
  buildMapfreTravelPayload,
  canRepriceTravelFromDobs,
  enrichTravelQuoteSnapshot,
  hasTravelPremium,
  inferAccountHolderTravelling,
  isTravelAdult,
  isTravelMinor,
  isValidPastDob,
  isValidTravelPassport,
  mergeTravelQuoteSnapshot,
  normalizeTravelDob,
  normalizeTravelPassport,
  parseQuoteTravellerDobs,
  reconcileTravelPolicyholder,
  sanitizeTravelPassportFields,
  serializeTravelTravellersForApi,
  travelPassportError,
  TRAVEL_PASSPORT_MAX_LEN,
  validateTravelPolicyholder,
  withTravelPricingPlaceholders,
} from '@/utils/travelPricing';
import { fetchIprsProfile } from '@/utils/motorIdentityVerify';
import { mapGetters } from 'vuex';
import {
  AGENT_EMAIL_MESSAGE,
  AGENT_PHONE_MESSAGE,
  agentContactError,
  isAgentEmail,
  isAgentPhone,
} from '@/utils/agentSelfContact';
import { trackAgentInteraction } from '@/utils/agentAnalytics';
import { getTravelChannelConfig } from '@/utils/travelChannel';
import { getCognitoIdentityProfile } from '@/utils/paChannel';
import {
  emptyTravelUploadState,
  uploadTravelPassport,
  validateTravelUploadFile,
} from '@/utils/travelDocumentUpload';

const REPRICE_DEBOUNCE_MS = 800;
const POLL_FIRST_DELAY_MS = 10000;
const POLL_INTERVAL_MS = 8000;
const POLL_MAX_ATTEMPTS = 45;
const SALE_MODE_SOLO_ADULT = 'solo-adult';
const SALE_MODE_GUARDIAN_MINOR = 'guardian-minor';
const SALE_MODE_STANDARD = 'standard';

function emptyTraveller(isMain = false) {
  return {
    firstName: '', surname: '', dateOfBirth: '', passport: '', isMain, isTraveling: true,
  };
}

export default {
  name: 'TravelSalePage',
  components: {
    UploadZone,
    TravelQuoteSidebar,
    TravelTravellerForm,
    TravelPaymentMethods,
    ReferralAgentBanner,
  },
  props: {
    channel: {
      type: String,
      required: true,
      validator: (v) => v === 'agent' || v === 'customer',
    },
  },
  data() {
    return {
      SALE_MODE_SOLO_ADULT,
      TRAVEL_PASSPORT_MAX_LEN,
      loading: true,
      quoteLoadError: '',
      activeStep: 0,
      steps: ['Travellers', 'Contact', 'Payment'],
      quote: {},
      customerEmail: '',
      phone: '',
      declaration: false,
      paying: false,
      polling: false,
      pollTimer: null,
      agentId: 0,
      activePaymentId: null,
      pollAttempts: 0,
      pollTimedOut: false,
      paybillFallback: false,
      verifyingPaybill: false,
      activePayTab: 'mpesa',
      travellers: [emptyTraveller(true)],
      repricing: false,
      priceStale: false,
      repriceTimer: null,
      repriceRequestId: 0,
      travellersPriced: false,
      quotedTravellerDobs: [],
      accountPolicyholder: null,
      policyholderIsTravelling: false,
      quotedPolicyholderTravelling: false,
      loadingPolicyholderIprs: false,
      policyholderIprsFetched: false,
      policyholderPassportUpload: emptyTravelUploadState(),
      showNewSaleConfirm: false,
      passportUploads: [],
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    travelConfig() {
      return getTravelChannelConfig({ travelChannel: this.channel });
    },
    quoteId() {
      return this.$route.query.quoteId || this.quote.quoteId;
    },
    travellerCount() {
      return Math.min(Math.max(this.quote.travellerCount || 1, 1), 10);
    },
    accountHolderTravelling() {
      // Only a signed-in customer can say they are not travelling; agent sales need a separate policyholder only when
      // every traveller is a minor (saleMode).
      if (this.channel !== 'customer') return true;
      return inferAccountHolderTravelling(this.quote || {});
    },
    saleMode() {
      if (!this.accountHolderTravelling) return SALE_MODE_GUARDIAN_MINOR;
      const dobs = this.quotedTravellerDobs;
      if (
        this.quote?.accountHolderDob
        && isTravelAdult(this.quote.accountHolderDob)
        && dobs.length
        && dobs.every((d) => isTravelMinor(d))
      ) {
        return SALE_MODE_GUARDIAN_MINOR;
      }
      if (!dobs.length) return 'pending';
      if (this.travellerCount === 1 && dobs.every((d) => isTravelAdult(d))) return SALE_MODE_SOLO_ADULT;
      const anyMinor = dobs.some((d) => isTravelMinor(d));
      const anyAdult = dobs.some((d) => isTravelAdult(d));
      if (anyMinor && !anyAdult) return SALE_MODE_GUARDIAN_MINOR;
      return SALE_MODE_STANDARD;
    },
    pricedTravellerCount() {
      if (this.accountPolicyholder && this.policyholderIsTravelling) {
        return this.travellerCount + 1;
      }
      return this.travellerCount;
    },
    maxPolicyholderDob() {
      return new Date().toISOString().slice(0, 10);
    },
    insuredsForMapfre() {
      if (this.accountPolicyholder) {
        const holder = {
          ...this.accountPolicyholder,
          isMain: true,
          isTraveling: this.policyholderIsTravelling,
        };
        const travelling = this.travellers.map((t) => ({
          ...t,
          isMain: false,
          isTraveling: true,
        }));
        return [holder, ...travelling];
      }
      return reconcileTravelPolicyholder(this.travellers);
    },
    policyholderLabel() {
      if (!this.accountPolicyholder) return 'Policyholder';
      return this.travellerLabel(this.accountPolicyholder);
    },
    policyholderPassportError() {
      if (!this.accountPolicyholder?.passport) return [];
      const err = travelPassportError(this.accountPolicyholder.passport);
      return err ? [err] : [];
    },
    travelingTravellers() {
      return this.travellers;
    },
    policyholderValidation() {
      return validateTravelPolicyholder(this.insuredsForMapfre);
    },
    summaryLines() {
      if (!this.quote.productLabel) return [];
      return [
        { label: 'Product', value: this.quote.productLabel },
        { label: 'Zone', value: ZONE_LABELS[this.quote.zone] || this.quote.zone },
        { label: 'Period', value: `${this.quote.startDate} → ${this.quote.endDate}` },
        { label: 'Travellers', value: String(this.pricedTravellerCount) },
        { label: 'Quote ref', value: String(this.quoteId || '—') },
      ];
    },
    travellersComplete() {
      const traveling = this.travellers;
      const travelingFieldsOk = traveling.every((t) => t.firstName
        && t.surname
        && isValidPastDob(t.dateOfBirth)
        && isValidTravelPassport(t.passport));
      const holderOk = !this.accountPolicyholder || (
        this.accountPolicyholder.firstName
        && this.accountPolicyholder.surname
        && isValidPastDob(this.accountPolicyholder.dateOfBirth)
        && isTravelAdult(this.accountPolicyholder.dateOfBirth)
        && isValidTravelPassport(this.accountPolicyholder.passport)
      );
      return traveling.length === this.travellerCount
        && travelingFieldsOk
        && holderOk
        && this.policyholderValidation.valid;
    },
    hasQuotedPremium() {
      return hasTravelPremium(this.quote);
    },
    agentContactIssue() {
      if (this.channel !== 'agent') return '';
      return agentContactError({ email: this.customerEmail, phone: this.phone }, this.authenticatedUser);
    },
    agentEmailError() {
      return this.channel === 'agent' && isAgentEmail(this.customerEmail, this.authenticatedUser)
        ? [AGENT_EMAIL_MESSAGE]
        : [];
    },
    agentPhoneError() {
      return this.channel === 'agent' && isAgentPhone(this.phone, this.authenticatedUser)
        ? [AGENT_PHONE_MESSAGE]
        : [];
    },
    canContinueFromContact() {
      return !!this.customerEmail
        && !!this.phone
        && !this.agentContactIssue
        && this.allPassportsUploaded()
        && !this.passportUploadsBusy;
    },
    contactContinueHint() {
      if (this.passportUploadsBusy) return 'Passport uploads in progress — please wait.';
      if (!this.customerEmail) return 'Enter customer email.';
      if (!this.phone) return 'Enter M-Pesa phone number.';
      if (this.agentContactIssue) return this.agentContactIssue;
      if (!this.allPassportsUploaded()) return 'Upload a passport copy for each traveller.';
      if (this.accountPolicyholder && !this.policyholderPassportUpload?.done) {
        return this.channel === 'customer'
          ? 'Upload your passport copy as policyholder.'
          : 'Upload policyholder passport copy.';
      }
      return '';
    },
    passportUploadsBusy() {
      return this.passportUploads.some((u) => u && u.loading);
    },
    canContinueFromTravellers() {
      return this.travellersComplete && !this.repricing && this.hasQuotedPremium;
    },
    travellersContinueHint() {
      if (this.repricing) return 'Updating premium — please wait.';
      if (!this.hasQuotedPremium) return 'Premium missing — go back to quote and get a price first.';
      if (this.travelingTravellers.length !== this.travellerCount) {
        return 'Traveller details are still loading.';
      }
      if (this.loadingPolicyholderIprs) return 'Loading your ID details…';
      if (this.accountPolicyholder) {
        if (this.channel === 'agent' && (!this.accountPolicyholder.firstName || !this.accountPolicyholder.surname)) {
          return 'Enter policyholder first name and surname.';
        }
        if (!this.accountPolicyholder.dateOfBirth || !isValidPastDob(this.accountPolicyholder.dateOfBirth)) {
          return this.channel === 'customer'
            ? 'Waiting for your date of birth from ID records.'
            : 'Enter the policyholder date of birth.';
        }
        if (!isTravelAdult(this.accountPolicyholder.dateOfBirth)) {
          return 'Policyholder must be at least 18 years old.';
        }
        if (!this.accountPolicyholder.passport) {
          return this.channel === 'customer'
            ? 'Enter your passport as policyholder.'
            : 'Enter policyholder passport.';
        }
        if (!isValidTravelPassport(this.accountPolicyholder.passport)) {
          return travelPassportError(this.accountPolicyholder.passport);
        }
      }
      const incomplete = this.travellers.find((t) => !t.firstName || !t.surname || !t.passport);
      if (incomplete) return 'Enter first name, surname and passport for each traveller.';
      const badPassport = this.travellers.find((t) => t.passport && !isValidTravelPassport(t.passport));
      if (badPassport) return travelPassportError(badPassport.passport);
      const badDob = this.travellers.find((t) => !isValidPastDob(t.dateOfBirth));
      if (badDob) return 'Each traveller needs a valid date of birth (not in the future).';
      if (!this.policyholderValidation.valid) return this.policyholderValidation.message;
      return '';
    },
    pricingNeedsRefresh() {
      if (this.dobsChangedFromQuote()) return true;
      if (this.accountPolicyholder
        && this.policyholderIsTravelling !== this.quotedPolicyholderTravelling) {
        return true;
      }
      return false;
    },
    canPay() {
      if (this.repricing || this.priceStale) return false;
      const premiumOk = this.hasQuotedPremium && Number(this.quote.kshAmount) > 0;
      const pricingOk = this.travellersPriced
        || (!this.pricingNeedsRefresh && premiumOk);
      return !!(
        this.declaration
        && this.customerEmail
        && this.phone
        && !this.agentContactIssue
        && premiumOk
        && this.travellersComplete
        && this.allPassportsUploaded()
        && pricingOk
      );
    },
    paymentBlockedHint() {
      if (this.repricing) return 'Updating premium — please wait.';
      if (this.priceStale) return 'Traveller dates changed — wait for premium to refresh.';
      if (!this.declaration) return 'Tick the customer confirmation checkbox to continue.';
      if (!this.customerEmail) return 'Enter customer email on the previous step.';
      if (!this.phone) return 'Enter M-Pesa phone on the previous step.';
      if (this.agentContactIssue) return `${this.agentContactIssue} — go back to the previous step.`;
      if (!this.hasQuotedPremium || !Number(this.quote.kshAmount)) {
        return 'Premium missing — go back and get a price first.';
      }
      if (!this.travellersComplete) {
        return 'Complete traveller names, passport and date of birth on step 1.';
      }
      if (!this.paymentReference) {
        return 'Enter the customer ID / passport number so Paybill account TR-{ID} can be generated.';
      }
      if (!this.allPassportsUploaded()) {
        return 'Upload passport copies for each traveller on step 2.';
      }
      if (!this.travellersPriced && this.pricingNeedsRefresh) {
        return 'Date of birth changed — wait for premium to update.';
      }
      return '';
    },
    customerIdNo() {
      const profile = this.channel === 'customer'
        ? getCognitoIdentityProfile(this.authenticatedUser)
        : null;
      const holder = this.accountPolicyholder;
      const main = this.travellers.find((t) => t.isMain) || this.travellers[0];
      return resolveTravelCustomerIdNo({
        idNumber: holder?.idNumber || main?.idNumber || profile?.idNumber,
        nationalId: holder?.nationalId || main?.nationalId,
        passport: holder?.passport || main?.passport,
      });
    },
    paymentReference() {
      return travelPaymentReference(this.customerIdNo);
    },
    formattedPremium() {
      return `KES ${this.formatMoney(this.quote.kshAmount)}`;
    },
    ussdSteps() {
      return [
        'Go to <strong>M-Pesa</strong> — open the <strong>M-Pesa App / SIM Toolkit</strong>, or dial <strong>*334#</strong>',
        'Select <strong>Lipa Na M-Pesa</strong> → <strong>Pay Bill</strong>',
        'Enter Business No. <strong>504700</strong>',
        `Enter Account No. <strong>${this.paymentReference || '—'}</strong>`,
        `Enter amount <strong>${this.formattedPremium}</strong>`,
        'Enter your <strong>M-Pesa PIN</strong> and confirm',
        'You will receive an <strong>M-Pesa confirmation SMS</strong> — then tap <strong>Validate payment</strong>',
      ];
    },
  },
  watch: {
    '$route.query.quoteId': {
      immediate: true,
      handler(newId, oldId) {
        if (!newId) return;
        if (String(newId) !== String(oldId || '')) {
          this.loadTravelQuote();
        }
      },
    },
    travellers: {
      deep: true,
      handler() {
        this.syncPassportUploadSlots();
      },
    },
    activeStep() { this.saveSaleProgress(); },
    customerEmail() { this.saveSaleProgress(); },
    phone() { this.saveSaleProgress(); },
    declaration() { this.saveSaleProgress(); },
  },
  async mounted() {
    if (this.travelConfig.trackAnalytics) {
      trackAgentInteraction(this, 'travel_sale_view', { quoteId: this.quoteId });
    }
    if (this.travelConfig.prefillContactFromProfile) {
      this.applyCustomerProfile();
    }
    const cachedAgentId = this.$store.getters['portalCache/agentNumericId'];
    if (this.channel === 'agent' && cachedAgentId != null && !Number.isNaN(Number(cachedAgentId))) {
      this.agentId = Number(cachedAgentId);
    }

    if (this.channel === 'agent') {
      agentTravelApi.resolveAgentNumericId(this.authenticatedUser).then((id) => {
        if (id != null && !Number.isNaN(id)) this.agentId = id;
      }).catch(() => {});
    } else {
      await this.$store.dispatch('referral/ensureReferralValidated', this.$route.query);
      const referredId = referralAgencyNumericId(this.$store.getters['referral/referralAgent']);
      if (referredId > 0) this.agentId = referredId;
    }
  },
  beforeDestroy() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
    if (this.repriceTimer) clearTimeout(this.repriceTimer);
  },
  methods: {
    formatMoney(n) {
      return Number(n || 0).toLocaleString();
    },
    travellerLabel(traveller) {
      const name = [traveller.firstName, traveller.surname].filter(Boolean).join(' ').trim();
      if (name) return name;
      if (traveller.passport) return traveller.passport;
      return 'Details pending';
    },
    syncPassportUploadSlots() {
      const count = this.travellers.length;
      while (this.passportUploads.length < count) {
        this.passportUploads.push(emptyTravelUploadState());
      }
      if (this.passportUploads.length > count) {
        this.passportUploads = this.passportUploads.slice(0, count);
      }
    },
    passportUploadFor(idx) {
      return this.passportUploads[idx] || emptyTravelUploadState();
    },
    allPassportsUploaded() {
      if (!this.travellers.length) return false;
      const travelingOk = this.travellers.every((_, idx) => {
        const slot = this.passportUploads[idx];
        return slot && slot.done && slot.file;
      });
      if (!this.accountPolicyholder) return travelingOk;
      const holderSlot = this.policyholderPassportUpload;
      return travelingOk && holderSlot && holderSlot.done && holderSlot.file;
    },
    handlePassportFileSelect(file, idx) {
      const check = validateTravelUploadFile(file);
      if (!check.valid) {
        this.$notify.error(check.message);
        return;
      }
      this.syncPassportUploadSlots();
      this.$set(this.passportUploads, idx, {
        file,
        loading: false,
        done: false,
      });
      this.uploadPassportFile(idx).catch(() => {});
    },
    clearPassportUpload(idx) {
      this.$set(this.passportUploads, idx, emptyTravelUploadState());
    },
    handlePolicyholderPassportSelect(file) {
      const check = validateTravelUploadFile(file);
      if (!check.valid) {
        this.$notify.error(check.message);
        return;
      }
      this.policyholderPassportUpload = { file, loading: false, done: false };
      this.uploadPolicyholderPassportFile().catch(() => {});
    },
    clearPolicyholderPassportUpload() {
      this.policyholderPassportUpload = emptyTravelUploadState();
    },
    async uploadPolicyholderPassportFile() {
      const slot = this.policyholderPassportUpload;
      const holder = this.accountPolicyholder;
      if (!slot?.file || slot.done || !holder) return;
      if (!this.customerEmail || !this.phone) {
        this.$toast.info('Enter email and phone before uploading passports');
        return;
      }
      this.policyholderPassportUpload = { ...slot, loading: true };
      try {
        await uploadTravelPassport({
          file: slot.file,
          fullName: this.policyholderLabel,
          email: this.customerEmail,
          phoneNumber: this.normalizePhone(this.phone),
          passportNumber: holder.passport,
          idNumber: holder.passport,
          travelType: 'pacis',
        });
        this.policyholderPassportUpload = { file: slot.file, loading: false, done: true };
      } catch (err) {
        this.policyholderPassportUpload = { file: slot.file, loading: false, done: false };
        this.$notify.fromError(err, 'Passport upload failed');
        throw err;
      }
    },
    async uploadPassportFile(idx) {
      const slot = this.passportUploads[idx];
      const traveller = this.travellers[idx];
      if (!slot?.file || slot.done || !traveller) return;
      if (!this.customerEmail || !this.phone) {
        this.$toast.info('Enter email and phone before uploading passports');
        return;
      }
      const uploadState = { ...slot, loading: true };
      this.$set(this.passportUploads, idx, uploadState);
      try {
        await uploadTravelPassport({
          file: slot.file,
          fullName: this.travellerLabel(traveller),
          email: this.customerEmail,
          phoneNumber: this.normalizePhone(this.phone),
          passportNumber: traveller.passport,
          idNumber: traveller.passport,
          travelType: 'pacis',
        });
        this.$set(this.passportUploads, idx, {
          file: slot.file,
          loading: false,
          done: true,
        });
      } catch (err) {
        this.$set(this.passportUploads, idx, {
          file: slot.file,
          loading: false,
          done: false,
        });
        this.$notify.fromError(err, 'Passport upload failed');
        throw err;
      }
    },
    async uploadPendingPassports() {
      const tasks = this.travellers.map((_, idx) => {
        const slot = this.passportUploads[idx];
        if (slot?.file && !slot.done) {
          return this.uploadPassportFile(idx);
        }
        return Promise.resolve();
      });
      const holderSlot = this.policyholderPassportUpload;
      if (holderSlot?.file && !holderSlot.done) {
        tasks.push(this.uploadPolicyholderPassportFile());
      }
      await Promise.all(tasks);
    },
    async continueFromContact() {
      if (!this.canContinueFromContact) return;
      try {
        await this.uploadPendingPassports();
      } catch (e) {
        return;
      }
      if (!this.allPassportsUploaded()) {
        this.$toast.error('Upload a passport copy for each traveller');
        return;
      }
      this.activeStep = 2;
    },
    applyCustomerProfile() {
      const profile = getCognitoIdentityProfile(this.authenticatedUser);
      if (!profile) return;
      if (!this.customerEmail && profile.email) this.customerEmail = profile.email;
      if (!this.phone && profile.phone) this.phone = profile.phone;
    },
    goBack() {
      this.$router.push({
        name: this.travelConfig.routes.planSelect,
        query: { journey: this.$route.query.journey },
      });
    },
    restoreSaleProgress(progress) {
      if (!progress) return;
      if (Number.isFinite(progress.activeStep)) this.activeStep = progress.activeStep;
      if (progress.customerEmail) this.customerEmail = progress.customerEmail;
      if (progress.phone) this.phone = progress.phone;
      if (progress.declaration) this.declaration = progress.declaration;
      if (progress.activePaymentId) this.activePaymentId = progress.activePaymentId;
      if (progress.accountPolicyholder) {
        this.accountPolicyholder = sanitizeTravelPassportFields({ ...progress.accountPolicyholder });
      }
      if (progress.policyholderIsTravelling != null) {
        this.policyholderIsTravelling = !!progress.policyholderIsTravelling;
      }
      if (progress.quotedPolicyholderTravelling != null) {
        this.quotedPolicyholderTravelling = !!progress.quotedPolicyholderTravelling;
      }
      if (progress.policyholderPassportUpload) {
        this.policyholderPassportUpload = { ...progress.policyholderPassportUpload };
      }
      if (Array.isArray(progress.travellers) && progress.travellers.length) {
        const holderFromSaved = progress.travellers.find(
          (t) => t.isTraveling === false || t.isAccountHolder,
        );
        if (!this.accountPolicyholder && holderFromSaved) {
          this.accountPolicyholder = {
            ...holderFromSaved,
            isMain: true,
            isTraveling: false,
            isAccountHolder: true,
          };
        }
        const travelingSaved = progress.travellers.filter((t) => t.isTraveling !== false);
        this.mergeSaleProgressTravellers(travelingSaved);
      }
      if (progress.travellersPriced === true || this.isPremiumLockedFromQuote()) {
        this.travellersPriced = true;
      }
      this.resolveCustomerSaleLayout();
      this.sanitizeStoredPassports();
      this.fetchCustomerIdentityFromIprs();
    },
    sanitizeStoredPassports() {
      if (this.accountPolicyholder) {
        this.accountPolicyholder = sanitizeTravelPassportFields(this.accountPolicyholder);
      }
      if (this.travellers.length) {
        this.travellers = this.travellers.map(sanitizeTravelPassportFields);
      }
    },
    mergeSaleProgressTravellers(saved) {
      const count = this.travellerCount;
      this.travellers = Array.from({ length: count }, (_, i) => {
        const quotedDob = normalizeTravelDob(this.quotedTravellerDobs[i]);
        const savedRow = saved[i] || {};
        const base = this.travellers[i] || emptyTraveller(false);
        return {
          ...base,
          ...savedRow,
          dateOfBirth: quotedDob || normalizeTravelDob(savedRow.dateOfBirth) || base.dateOfBirth,
          dobFromQuote: !!quotedDob,
          isMain: false,
          isTraveling: true,
        };
      });
      this.syncPassportUploadSlots();
    },
    startNewSale() {
      const email = this.authenticatedUser?.email;
      clearTravelQuoteCache(email, this.quoteId);
      this.showNewSaleConfirm = false;
      this.$router.push({
        name: this.travelConfig.routes.planSelect,
        query: {
          journey: this.$route.query.journey || this.travelConfig.defaultJourney,
          reset: 'true',
        },
      });
    },
    async loadTravelQuote() {
      this.loading = true;
      this.quoteLoadError = '';
      try {
        await this.$nextTick();
        const routeQuoteId = this.$route.query.quoteId;
        const quoteId = Array.isArray(routeQuoteId) ? routeQuoteId[0] : routeQuoteId;
        if (!quoteId) {
          this.quoteLoadError = 'No quote reference in the link. Go back and start again.';
          return;
        }

        const email = this.authenticatedUser?.email;
        const cached = loadTravelPendingQuote(email);
        const cacheHit = cached && String(cached.quoteId) === String(quoteId)
          ? enrichTravelQuoteSnapshot(cached)
          : null;

        if (cacheHit && hasTravelPremium(cacheHit)) {
          this.applyQuoteSnapshot(cacheHit);
        }

        const saleProgress = loadTravelSaleProgress(email, quoteId);

        if (isRemoteTravelQuoteId(quoteId)) {
          const res = await agentTravelApi.getQuote(quoteId);
          if (res.success && res.data) {
            this.applyQuoteSnapshot(mergeTravelQuoteSnapshot(cacheHit, res.data, quoteId));
            this.restoreSaleProgress(saleProgress);
            return;
          }
          if (cacheHit && hasTravelPremium(cacheHit)) {
            this.restoreSaleProgress(saleProgress);
            return;
          }
          this.quoteLoadError = res.message || 'Could not load this travel quote.';
          if (res.message) this.showToast('error', res.message, 'Quote');
          return;
        }

        if (cacheHit) {
          this.applyQuoteSnapshot(cacheHit);
          this.restoreSaleProgress(saleProgress);
          return;
        }

        this.quoteLoadError = 'Quote not found in this session. Go back and get a new price.';
      } catch (e) {
        this.quoteLoadError = 'Could not load travel quote. Try going back to the quote step.';
        this.showToast('error', 'Could not load travel quote', 'Quote');
      } finally {
        this.loading = false;
        if (!this.quoteLoadError && !hasTravelPremium(this.quote)) {
          this.quoteLoadError = 'This quote has no premium yet. Go back and complete the quote step.';
        }
      }
    },
    applyQuoteSnapshot(snapshot) {
      const prevEmail = this.customerEmail;
      const prevPhone = this.phone;
      this.quote = enrichTravelQuoteSnapshot({ ...snapshot });
      this.customerEmail = this.quote.customerEmail || this.quote.email_address || this.quote.email || prevEmail || '';
      this.phone = this.quote.phone || this.quote.phone_number || prevPhone || '';
      this.quotedTravellerDobs = parseQuoteTravellerDobs(this.quote);
      this.quotedPolicyholderTravelling = false;
      this.policyholderIsTravelling = false;
      this.policyholderIprsFetched = false;
      this.initTravellers(this.quote);
      this.travellersPriced = this.isPremiumLockedFromQuote();
      this.priceStale = false;
    },
    initTravellers(cached) {
      const count = Math.min(Math.max(cached.travellerCount || cached.traveller_count || 1, 1), 10);
      let sourceRows = Array.isArray(cached.travellers) ? cached.travellers : [];
      const holderRow = sourceRows.find((t) => t.isTraveling === false || t.isAccountHolder);
      if (holderRow) {
        if (this.channel === 'customer') {
          this.accountPolicyholder = {
            ...buildAccountHolderTraveller(getCognitoIdentityProfile(this.authenticatedUser) || {}),
            ...holderRow,
            isMain: true,
            isTraveling: false,
            isAccountHolder: true,
          };
        } else {
          this.accountPolicyholder = {
            firstName: holderRow.firstName || '',
            surname: holderRow.surname || '',
            dateOfBirth: normalizeTravelDob(holderRow.dateOfBirth) || '',
            passport: holderRow.passport || '',
            isMain: true,
            isTraveling: false,
            isAccountHolder: true,
          };
        }
      }
      sourceRows = sourceRows.filter((t) => t.isTraveling !== false && !t.isAccountHolder);
      const quoted = sourceRows.slice(0, count);
      const dobs = this.quotedTravellerDobs.length
        ? this.quotedTravellerDobs
        : quoted.map((t) => normalizeTravelDob(t.dateOfBirth)).filter(Boolean);
      const existing = quoted.filter(
        (t) => t.firstName && !String(t.firstName).startsWith('Traveller'),
      );
      if (existing.length) {
        const filled = JSON.parse(JSON.stringify(existing.slice(0, count)));
        while (filled.length < count) filled.push(emptyTraveller(false));
        if (!filled.some((t) => t.isMain) && this.accountHolderTravelling) filled[0].isMain = true;
        this.travellers = filled.map((t, i) => {
          const quotedDob = normalizeTravelDob(dobs[i]);
          return {
            ...t,
            dateOfBirth: quotedDob || normalizeTravelDob(t.dateOfBirth),
            dobFromQuote: !!quotedDob,
          };
        });
        this.syncPassportUploadSlots();
        this.resolveCustomerSaleLayout();
        this.fetchCustomerIdentityFromIprs();
        return;
      }
      this.travellers = Array.from({ length: count }, (_, i) => {
        const quotedDob = normalizeTravelDob(dobs[i] || quoted[i]?.dateOfBirth);
        const selfNames = i === 0 && this.accountHolderTravelling
          ? {
            firstName: cached.accountHolderFirstName || quoted[i]?.firstName || '',
            surname: cached.accountHolderSurname || quoted[i]?.surname || '',
            identityFromIprs: !!(cached.accountHolderFirstName || cached.accountHolderSurname),
          }
          : {};
        return {
          ...emptyTraveller(false),
          ...selfNames,
          dateOfBirth: quotedDob,
          dobFromQuote: !!quotedDob,
          isMain: this.accountHolderTravelling ? (quoted[i]?.isMain ?? i === 0) : false,
        };
      });
      this.syncPassportUploadSlots();
      this.resolveCustomerSaleLayout();
      this.sanitizeStoredPassports();
      this.fetchCustomerIdentityFromIprs();
    },
    dobsChangedFromQuote() {
      if (!this.quotedTravellerDobs.length) return false;
      return this.travelingTravellers.some(
        (t, i) => t.dateOfBirth
          && normalizeTravelDob(t.dateOfBirth) !== normalizeTravelDob(this.quotedTravellerDobs[i]),
      );
    },
    isPremiumLockedFromQuote() {
      return !!(
        this.hasQuotedPremium
        && this.quotedTravellerDobs.length > 0
        && !this.pricingNeedsRefresh
      );
    },
    onTravellersUpdate(updated) {
      this.travellers = this.accountPolicyholder
        ? updated.map((t) => ({ ...t, isMain: false, isTraveling: true }))
        : reconcileTravelPolicyholder(updated);
      if (this.pricingNeedsRefresh) {
        this.travellersPriced = false;
        this.priceStale = true;
        if (this.travellersComplete) this.scheduleReprice();
      } else {
        this.travellersPriced = this.isPremiumLockedFromQuote();
        this.priceStale = false;
      }
      if (hasTravelPremium(this.quote)) this.persistState();
    },
    onPolicyholderError(message) {
      this.showToast('warning', message, 'Policyholder');
    },
    resolveCustomerSaleLayout() {
      if (this.saleMode === SALE_MODE_GUARDIAN_MINOR) {
        if (!this.accountPolicyholder) {
          if (this.channel === 'customer') {
            const profile = getCognitoIdentityProfile(this.authenticatedUser);
            this.accountPolicyholder = buildAccountHolderTraveller(profile);
          } else {
            this.accountPolicyholder = {
              firstName: this.quote?.accountHolderFirstName || '',
              surname: this.quote?.accountHolderSurname || '',
              dateOfBirth: this.quote?.accountHolderDob || '',
              passport: '',
              isMain: true,
              isTraveling: false,
              isAccountHolder: true,
            };
          }
          if (this.quote?.accountHolderDob) {
            this.accountPolicyholder.dateOfBirth = this.quote.accountHolderDob;
          }
          if (this.quote?.accountHolderFirstName) {
            this.accountPolicyholder.firstName = this.quote.accountHolderFirstName;
          }
          if (this.quote?.accountHolderSurname) {
            this.accountPolicyholder.surname = this.quote.accountHolderSurname;
          }
        }
        this.travellers = this.travellers.slice(0, this.travellerCount).map((t) => ({
          ...t,
          isMain: false,
          isTraveling: true,
        }));
        return;
      }
      const pricedWithHolder = !!this.accountPolicyholder && this.quotedPolicyholderTravelling;
      this.accountPolicyholder = null;
      this.policyholderIsTravelling = false;
      this.quotedPolicyholderTravelling = false;
      if (pricedWithHolder) {
        this.travellersPriced = false;
        this.priceStale = true;
        this.$nextTick(() => this.scheduleReprice({ pricingOnly: true }));
      }
      if (this.saleMode === SALE_MODE_SOLO_ADULT && this.travellers.length === 1) {
        this.travellers = [{
          ...this.travellers[0],
          isMain: true,
          isTraveling: true,
        }];
        return;
      }
      this.travellers = reconcileTravelPolicyholder(this.travellers);
    },
    async fetchCustomerIdentityFromIprs() {
      if (this.channel !== 'customer') return;
      const profile = getCognitoIdentityProfile(this.authenticatedUser);
      if (!profile?.idNumber) return;

      const holderHasDob = this.accountPolicyholder?.dateOfBirth;

      if (this.saleMode === SALE_MODE_GUARDIAN_MINOR && this.accountPolicyholder) {
        if (this.policyholderIprsFetched && holderHasDob) return;
        this.loadingPolicyholderIprs = true;
        try {
          const res = await fetchIprsProfile(profile.idNumber, profile.firstName);
          if (res.iprs) {
            this.accountPolicyholder = {
              ...applyIprsToTravellerRow(this.accountPolicyholder, res.iprs),
              idNumber: profile.idNumber,
              isMain: true,
              isTraveling: false,
              isAccountHolder: true,
            };
          } else if (res.iprsUnavailable) {
            this.$toast.info('Could not load ID details — enter your date of birth manually.');
          }
        } finally {
          this.loadingPolicyholderIprs = false;
          this.policyholderIprsFetched = true;
          if (this.accountPolicyholder?.dateOfBirth && this.policyholderIsTravelling) {
            this.scheduleReprice({ pricingOnly: true });
          }
        }
        return;
      }

      if (!this.accountHolderTravelling) return;

      const mainIdx = this.travellers.findIndex((t) => t.isMain);
      const idx = mainIdx >= 0 ? mainIdx : 0;
      const row = this.travellers[idx];
      if (!row) return;

      if (this.policyholderIprsFetched && row.firstName && row.surname) return;

      this.loadingPolicyholderIprs = true;
      try {
        const res = await fetchIprsProfile(profile.idNumber, profile.firstName);
        if (res.iprs) {
          const updated = applyIprsToTravellerRow(row, res.iprs, {
            keepDateOfBirth: !!row.dobFromQuote || !!normalizeTravelDob(row.dateOfBirth),
          });
          const next = [...this.travellers];
          next[idx] = {
            ...updated,
            isMain: row.isMain ?? idx === 0,
            isTraveling: row.isTraveling !== false,
            dobFromQuote: row.dobFromQuote,
            identityFromIprs: true,
          };
          this.travellers = next;
        }
      } finally {
        this.loadingPolicyholderIprs = false;
        this.policyholderIprsFetched = true;
      }
    },
    onPolicyholderTravellingChange() {
      this.travellersPriced = false;
      this.scheduleReprice({ pricingOnly: true });
      this.persistState();
    },
    scheduleReprice({ pricingOnly = false } = {}) {
      if (this.repriceTimer) clearTimeout(this.repriceTimer);
      this.repriceTimer = setTimeout(
        () => this.repriceWithMapfre({ pricingOnly }),
        REPRICE_DEBOUNCE_MS,
      );
    },
    buildIssuePayload({ pricingOnly = false } = {}) {
      const rawInsureds = pricingOnly
        ? withTravelPricingPlaceholders(this.insuredsForMapfre)
        : this.insuredsForMapfre;
      const payload = buildMapfreTravelPayload({
        prodCode: this.quote.prodCode,
        zone: this.quote.zone,
        destinationCountry: this.quote.destinationCountry,
        startDate: this.quote.startDate,
        endDate: this.quote.endDate,
        agnShtDesc: this.quote.agnShtDesc,
        email: this.customerEmail,
        insureds: rawInsureds,
        skipPolicyholderValidation: pricingOnly,
      });
      payload.premium = String(this.quote.grossUsd);
      return payload;
    },
    canRepriceFromDobs() {
      return canRepriceTravelFromDobs(this.insuredsForMapfre);
    },
    updateAccountPolicyholder(field, value) {
      if (!this.accountPolicyholder) return;
      const nextValue = field === 'passport' ? normalizeTravelPassport(value) : value;
      this.accountPolicyholder = { ...this.accountPolicyholder, [field]: nextValue };
      if (field === 'dateOfBirth') {
        this.travellersPriced = false;
        this.priceStale = true;
        if (this.travellersComplete) {
          this.scheduleReprice();
        } else if (this.canRepriceFromDobs()) {
          this.scheduleReprice({ pricingOnly: true });
        }
      } else {
        this.travellersPriced = this.isPremiumLockedFromQuote();
        this.priceStale = this.pricingNeedsRefresh;
      }
      this.persistState();
    },
    buildQuoteSalePayload() {
      const insureds = this.insuredsForMapfre;
      const main = insureds.find((t) => t.isMain) || insureds[0] || {};
      return {
        email_address: this.customerEmail,
        phone_number: this.normalizePhone(this.phone),
        first_name: main.firstName,
        last_name: main.surname,
        passport_no: main.passport,
        premium: this.quote.grossUsd,
        revised_price: this.quote.grossUsd,
        ksh_amount: this.quote.kshAmount,
        fx_rate: this.quote.fxRate,
        traveller_count: this.travellerCount,
        travellers: serializeTravelTravellersForApi(insureds),
        mapfreIssuePayloadJson: JSON.stringify(this.buildIssuePayload()),
        stepCode: 'TRAVEL_PAYMENT',
      };
    },
    saveSaleProgress() {
      if (!this.quoteId || !this.authenticatedUser?.email) return;
      saveTravelSaleProgress(this.authenticatedUser.email, this.quoteId, {
        activeStep: this.activeStep,
        customerEmail: this.customerEmail,
        phone: this.phone,
        declaration: this.declaration,
        activePaymentId: this.activePaymentId,
        travellers: JSON.parse(JSON.stringify(this.travellers)),
        accountPolicyholder: this.accountPolicyholder
          ? JSON.parse(JSON.stringify(this.accountPolicyholder))
          : null,
        policyholderIsTravelling: this.policyholderIsTravelling,
        quotedPolicyholderTravelling: this.quotedPolicyholderTravelling,
        policyholderPassportUpload: this.policyholderPassportUpload,
        travellersPriced: this.travellersPriced,
      });
    },
    async syncQuoteToServer() {
      const payload = this.buildQuoteSalePayload();
      const res = await agentTravelApi.updateQuoteDetails(this.quoteId, payload);
      if (!res.success && !res.skipped) {
        this.$toast.warning(res.message || 'Could not save quote details to server');
      }
      if (this.quote.pricingData) {
        await agentTravelApi.updateQuotePricing(this.quoteId, this.quote.pricingData).catch(() => {});
      }
      return res;
    },
    async repriceWithMapfre({ pricingOnly = false } = {}) {
      if (!this.quote.prodCode) return;
      if (!pricingOnly && !this.travellersComplete) return;
      if (!this.canRepriceFromDobs()) return;
      if (!pricingOnly && !this.policyholderValidation.valid) {
        this.$toast.warning(this.policyholderValidation.message, 'Policyholder');
        return;
      }
      this.repriceRequestId += 1;
      const requestId = this.repriceRequestId;
      this.repricing = true;
      try {
        const res = await agentTravelApi.getPrice(this.buildIssuePayload({ pricingOnly }));
        if (requestId !== this.repriceRequestId) return;
        if (!res.success || !res.data) {
          this.$toast.error(res.message || 'Could not update premium');
          return;
        }
        const grossUsd = res.data.grossPrice || res.data.netPrice;
        const kshAmount = Math.round(Number(grossUsd) * Number(this.quote.fxRate || 130));
        this.quote = {
          ...this.quote,
          grossUsd,
          kshAmount,
          pricingData: res.data,
          mapfrePayload: this.buildIssuePayload(),
        };
        this.travellersPriced = true;
        this.priceStale = false;
        this.quotedPolicyholderTravelling = this.policyholderIsTravelling;
        await agentTravelApi.updateQuotePricing(this.quoteId, res.data).catch(() => {});
        this.persistState();
      } finally {
        if (requestId === this.repriceRequestId) this.repricing = false;
      }
    },
    persistState() {
      try {
        const updated = {
          ...this.quote,
          customerEmail: this.customerEmail,
          travellers: JSON.parse(JSON.stringify(this.travellers)),
          travellerCount: this.travellerCount,
          travellersPriced: this.travellersPriced,
          quoteTravellerDobs: this.quotedTravellerDobs.length
            ? [...this.quotedTravellerDobs]
            : this.travellers.map((t) => t.dateOfBirth),
          mapfrePayload: this.safeMapfrePayload(),
          summaryLines: this.summaryLines,
          accountHolderDob: this.accountPolicyholder?.dateOfBirth || this.quote.accountHolderDob,
          accountHolderFirstName: this.accountPolicyholder?.firstName || this.quote.accountHolderFirstName,
          accountHolderSurname: this.accountPolicyholder?.surname || this.quote.accountHolderSurname,
        };
        this.quote = enrichTravelQuoteSnapshot(updated);
        saveTravelPendingQuote(this.authenticatedUser?.email, this.quote);
        this.saveSaleProgress();
      } catch (e) {
        // Avoid breaking the sale UI when Mapfre payload is not ready yet.
      }
    },
    safeMapfrePayload() {
      if (!this.quote?.prodCode || !hasTravelPremium(this.quote)) {
        return this.quote?.mapfrePayload || null;
      }
      try {
        return this.buildIssuePayload({ pricingOnly: true });
      } catch {
        return this.quote?.mapfrePayload || null;
      }
    },
    showToast(type, message, title) {
      const toast = this.$toast?.[type];
      if (toast) {
        toast(message, title);
        return;
      }
      const notifyType = type === 'warning' ? 'warn' : type;
      const notify = this.$notify?.[notifyType];
      if (notify) notify(message, title);
    },
    finishPaymentSuccess(email, message) {
      clearTravelQuoteCache(email, this.quoteId);
      invalidateAfterPayment(this.$store, this.channel === 'agent' ? 'agent' : 'customer');
      this.$toast.success(message || 'Payment received — issuing policy');
      this.$router.push({
        name: this.travelConfig.routes.finalization,
        params: { quoteId: String(this.quoteId) },
        query: {
          kshAmount: String(this.quote.kshAmount || ''),
          productLabel: this.quote.productLabel || '',
        },
      });
    },
    async verifyPaybillPayment() {
      if (!this.canPay || !this.quoteId) {
        if (this.paymentBlockedHint) {
          this.$toast.warning(this.paymentBlockedHint);
        }
        return;
      }
      this.verifyingPaybill = true;
      if (this.travelConfig.trackAnalytics) {
        trackAgentInteraction(this, 'travel_payment_validate_paybill', { quoteId: this.quoteId });
      }
      try {
        if (!this.travellersPriced) {
          await this.repriceWithMapfre();
          if (!this.travellersPriced) {
            this.$toast.error('Premium could not be updated. Check traveller details and try again.');
            return;
          }
        }
        const syncRes = await this.syncQuoteToServer();
        if (!syncRes.success && !syncRes.skipped) {
          this.$toast.warning(syncRes.message || 'Could not save quote details before validating payment');
        }
        const ready = await agentTravelApi.markReadyForPayment(this.quoteId);
        if (!ready.success && !ready.skipped) {
          this.$toast.warning(ready.message || 'Could not prepare quote for payment');
        }
        const valRes = await agentTravelApi.confirmTravelPaybillPayment({
          quoteId: this.quoteId,
          accountNumber: this.paymentReference,
          customerIdNo: this.customerIdNo,
        });
        if (!valRes.success) {
          this.$toast.error(valRes.message || 'Payment not yet received. Complete Paybill and try again.');
          return;
        }
        if (valRes.alreadyPaid) {
          this.$toast.info(valRes.message || 'This quote is already paid.');
        }
        this.finishPaymentSuccess(this.authenticatedUser?.email, valRes.message);
      } catch (e) {
        const msg = e?.response?.data?.msg
          || e?.response?.data?.message
          || e?.message
          || 'Payment validation failed';
        this.$toast.error(msg);
      } finally {
        this.verifyingPaybill = false;
      }
    },
    normalizePhone(raw) {
      const p = String(raw || '').replace(/\D/g, '');
      if (p.startsWith('254')) return p;
      if (p.startsWith('0') && p.length === 10) return `254${p.slice(1)}`;
      if (p.startsWith('7') && p.length === 9) return `254${p}`;
      return p;
    },
    async requestPayment() {
      if (!this.canPay) return;
      if (!this.travellersPriced) {
        await this.repriceWithMapfre();
        if (!this.travellersPriced) return;
      }
      await this.syncQuoteToServer();
      const ready = await agentTravelApi.markReadyForPayment(this.quoteId);
      if (!ready.success && !ready.skipped) {
        this.$toast.warning(ready.message || 'Could not prepare quote for payment');
      }
      this.persistState();
      this.paying = true;
      this.pollTimedOut = false;
      this.pollAttempts = 0;
      this.paybillFallback = false;
      if (this.travelConfig.trackAnalytics) {
        trackAgentInteraction(this, 'travel_stk_request', { quoteId: this.quoteId });
      }
      try {
        const res = await agentTravelApi.requestTravelStkPush({
          quoteId: this.quoteId,
          payableAmount: this.quote.kshAmount,
          customerPhoneNumber: this.normalizePhone(this.phone),
          transactionReference: this.paymentReference,
          customerIdNo: this.customerIdNo,
          transactionDescription: `Travel Insurance - ${this.quoteId}`,
          agentId: this.agentId,
          channel: this.channel,
        });
        if (!res.success) {
          this.$toast.error(res.message || 'STK request failed');
          this.paybillFallback = true;
          this.activePayTab = 'paybill';
          return;
        }
        this.activePaymentId = res.data?.paymentId || res.data?.payment_id;
        this.saveSaleProgress();
        this.$toast.success('Check phone to complete payment');
        this.polling = true;
        this.pollTimer = setTimeout(() => this.checkPayment(this.activePaymentId), POLL_FIRST_DELAY_MS);
      } finally {
        this.paying = false;
      }
    },
    manualCheckPayment() {
      if (!this.activePaymentId) return;
      this.pollTimedOut = false;
      this.polling = true;
      this.checkPayment(this.activePaymentId, true);
    },
    async checkPayment(paymentId, manual = false) {
      if (!paymentId) return;
      if (!manual) {
        this.pollAttempts += 1;
        if (this.pollAttempts > POLL_MAX_ATTEMPTS) {
          this.polling = false;
          this.pollTimedOut = true;
          this.paybillFallback = true;
          this.activePayTab = 'paybill';
          this.$toast.warning('Payment check timed out. Use Paybill or check status after the customer pays.');
          return;
        }
      }
      const res = await agentTravelApi.pollPaymentStatus(paymentId, this.channel);
      const status = res.data?.paymentStatus;
      if (status === 'SUCCESSFUL') {
        this.polling = false;
        this.pollTimedOut = false;
        this.finishPaymentSuccess(this.authenticatedUser?.email);
        return;
      }
      if (status === 'FAILED' || status === 'CANCELLED') {
        this.polling = false;
        this.pollTimedOut = false;
        this.paybillFallback = true;
        this.activePayTab = 'paybill';
        this.$toast.error('Payment failed or cancelled — try Paybill if needed');
        return;
      }
      if (manual) {
        this.$toast.info('Payment not received yet. Ask the customer to complete M-Pesa.');
        this.polling = false;
        return;
      }
      this.pollTimer = setTimeout(() => this.checkPayment(paymentId), POLL_INTERVAL_MS);
    },
  },
  metaInfo: { title: 'Travel — Complete Sale' },
};
</script>

<style scoped lang="scss">
@import '@/styles/agent-pa-form.scss';
@import '@/styles/customer-payment.scss';

.travel-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px 28px 40px;
}

.travel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 20px;
}

.travel-header-left {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
}

.btn-new-sale {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
  color: #1E4276;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
}

.btn-new-sale:hover { background: #EEF2FF; }

.new-sale-dialog {
  background: white;
  border-radius: 14px;
  box-shadow: 0 20px 40px rgba(2, 6, 23, 0.25);
  overflow: hidden;
}

.nsd-header {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 16px 16px 10px;
  border-bottom: 1px solid #E2E8F0;
}

.nsd-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nsd-title { margin: 0; font-size: 1rem; font-weight: 700; color: #0F172A; }
.nsd-sub { margin: 2px 0 0; font-size: 0.8rem; color: #64748B; }
.nsd-body { padding: 12px 16px; }
.nsd-desc { margin: 0; font-size: 0.82rem; color: #334155; line-height: 1.5; }

.nsd-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 16px 16px;
}

.nsd-btn-cancel, .nsd-btn-confirm {
  border: none;
  border-radius: 9px;
  padding: 10px 14px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
}

.nsd-btn-cancel { background: #F1F5F9; color: #334155; }
.nsd-btn-cancel:hover { background: #E2E8F0; }

.nsd-btn-confirm {
  background: linear-gradient(135deg, #00AEEF, #1E4276);
  color: white;
}

.nsd-btn-confirm:hover { filter: brightness(0.95); }

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid #E2E8F0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.breadcrumb { font-size: 0.72rem; color: #94A3B8; margin: 0; text-transform: uppercase; letter-spacing: 0.04em; }
.title { font-size: 1.35rem; font-weight: 800; color: #1E4276; margin: 4px 0 0; }

.step-progress {
  display: flex;
  gap: 8px;
  margin-bottom: 22px;
  flex-wrap: wrap;
}

.step-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: #F1F5F9;
  color: #64748B;
  font-size: 0.78rem;
  font-weight: 600;
}

.step-item.active {
  background: #E0F2FE;
  color: #1E4276;
}

.step-item.done {
  background: #D1FAE5;
  color: #065F46;
}

.step-dot {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 800;
}

.loading-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 48px;
  color: #64748B;
}

.loading-wrap--error {
  align-items: flex-start;
  color: #334155;
}

.load-error-text {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  max-width: 420px;
}

.quote-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 22px;
  align-items: start;
}

.section-card {
  background: white;
  border-radius: 14px;
  padding: 22px 24px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 2px 10px rgba(30, 66, 118, 0.05);
}

.section-header { display: flex; align-items: center; margin-bottom: 8px; }

.section-number {
  width: 28px; height: 28px; border-radius: 50%;
  background: linear-gradient(135deg, #00AEEF, #0284C7);
  color: white; font-weight: 800; font-size: 0.75rem;
  display: inline-flex; align-items: center; justify-content: center; margin-right: 10px;
}

.section-header h3 { margin: 0; font-size: 1.05rem; color: #1E4276; font-weight: 800; }

.step-hint {
  font-size: 0.82rem;
  color: #64748B;
  margin: 0 0 16px;
  line-height: 1.45;
}

.step-hint--warn {
  color: #D97706;
  margin-top: 10px;
}

.reprice-hint, .reprice-warn {
  font-size: 0.8rem;
  margin: 12px 0 0;
  display: flex;
  align-items: center;
}

.passport-uploads {
  border-top: 1px solid #F1F5F9;
  padding-top: 16px;
}

.passport-upload-card {
  margin-bottom: 14px;
}

.passport-upload-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: #475569;
  margin: 0 0 8px;
}

.subsection-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0 0 8px;
}

.policyholder-card {
  border: 1px solid #DBEAFE;
  border-radius: 10px;
  padding: 14px 16px 6px;
  margin-bottom: 14px;
  background: #F0F9FF;
}

.policyholder-card .req { color: #DC2626; }

.travelling-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  font-size: 0.84rem;
  color: #334155;
  cursor: pointer;
  user-select: none;
}

.travelling-toggle input {
  width: 16px;
  height: 16px;
  accent-color: #00AEEF;
}

.travelling-toggle-hint {
  margin: 4px 0 0 24px;
  font-size: 0.75rem;
  color: #64748B;
}

.reprice-hint { color: #00AEEF; }
.reprice-warn { color: #D97706; }

.actions-row {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #F1F5F9;
}

.btn-outline, .btn-primary, .btn-pay {
  border-radius: 10px;
  padding: 11px 20px;
  font-weight: 700;
  font-size: 0.88rem;
  cursor: pointer;
  border: none;
}

.btn-outline {
  background: white;
  border: 1.5px solid #E2E8F0;
  color: #475569;
}

.btn-primary {
  background: #00AEEF;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 174, 239, 0.3);
}

.btn-primary:disabled, .btn-pay:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-pay {
  background: linear-gradient(135deg, #184679, #1E4276);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
}

.pay-usd-note {
  font-size: 0.72rem;
  color: #64748B;
  margin: 4px 0 0;
  text-align: right;
}

.poll-hint { font-size: 0.8rem; color: #00AEEF; margin-top: 10px; text-align: center; }
.poll-hint--warn { color: #D97706; }

.agent-block-banner {
  padding: 12px 14px;
  border-radius: 10px;
  background: #FEF3C7;
  border: 1px solid #FCD34D;
  color: #92400E;
  font-size: 0.82rem;
  line-height: 1.4;
}

.decl-text { font-size: 0.82rem; color: #334155; line-height: 1.4; }

.quote-sidebar { position: sticky; top: 20px; }

@media (max-width: 960px) {
  .travel-page { padding: 16px 16px 28px; }
  .quote-layout { grid-template-columns: 1fr; gap: 16px; }
  .quote-sidebar { position: static; }
  .section-card { padding: 16px; }
}

@media (max-width: 600px) {
  .travel-page { padding: 12px 12px 88px; }
  .travel-header {
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }
  .travel-header-left { min-width: 0; flex: 1 1 auto; }
  .title { font-size: 1.15rem; line-height: 1.3; }
  .breadcrumb { font-size: 0.68rem; }
  .btn-new-sale {
    flex-shrink: 0;
    padding: 7px 10px;
    font-size: 0.72rem;
  }

  .step-progress {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 6px;
    margin-bottom: 14px;
  }
  .step-item {
    flex-direction: column;
    justify-content: center;
    gap: 4px;
    padding: 8px 4px;
    border-radius: 10px;
    text-align: center;
    min-width: 0;
  }
  .step-label {
    font-size: 0.58rem;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
  .step-dot {
    width: 20px;
    height: 20px;
    font-size: 0.65rem;
  }

  .actions-row {
    flex-direction: column-reverse;
    gap: 10px;
  }
  .actions-row .btn-outline,
  .actions-row .btn-primary,
  .actions-row .btn-pay {
    width: 100%;
    justify-content: center;
    min-height: 46px;
  }

  .nsd-footer {
    flex-direction: column-reverse;
    gap: 8px;
  }
  .nsd-footer > * {
    width: 100%;
    justify-content: center;
  }

  .pay-sum-row {
    flex-wrap: wrap;
    align-items: flex-start;
  }
  .pay-sum-value {
    text-align: left;
    word-break: break-word;
  }
}
</style>
